import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Loader2, Trophy, FileText, ImageIcon, Download, X, RefreshCw } from "lucide-react";
import ClassToppersPoster from "@/components/announcement/ClassToppersPoster";
import { exportImage, exportPdf } from "@/components/announcement/exportUtils";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANK_LABEL = { 1: "1st Rank", 2: "2nd Rank", 3: "3rd Rank" };

export default function ToppersGenerator({ onBack }) {
  const [options, setOptions] = useState(null);
  const [sectionKey, setSectionKey] = useState("");
  const [examKey, setExamKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);       // full poster data (with edited ranks)
  const [roster, setRoster] = useState([]);
  const [exporting, setExporting] = useState(false);
  const posterRef = useRef(null);
  const previewWrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, (el.clientWidth - 4) / 1123));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    api.get("/notices/toppers/options")
      .then(({ data }) => setOptions(data))
      .catch(() => toast.error("Could not load classes & exams."));
  }, []);

  const [className, section] = useMemo(() => sectionKey.split("|"), [sectionKey]);

  const load = async () => {
    if (!sectionKey || !examKey) { toast.error("Pick a class-section and an exam."); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get("/notices/toppers/data", {
        params: { class_name: className, section, exam_key: examKey },
      });
      setData(d);
      setRoster(d.roster || []);
      const has = (d.ranks || []).some((r) => (r.students || []).length > 0);
      if (!has) toast.message("No marks found for this class & exam yet — you can add toppers manually.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load toppers.");
    } finally {
      setLoading(false);
    }
  };

  // ── Editing helpers ──
  const usedIds = useMemo(() => {
    const ids = new Set();
    (data?.ranks || []).forEach((r) => (r.students || []).forEach((s) => s.student_id && ids.add(s.student_id)));
    return ids;
  }, [data]);

  const updateStudent = (rank, idx, field, value) => {
    setData((prev) => {
      const ranks = prev.ranks.map((r) => {
        if (r.rank !== rank) return r;
        const students = r.students.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
        return { ...r, students };
      });
      return { ...prev, ranks };
    });
  };
  const removeStudent = (rank, idx) => {
    setData((prev) => ({
      ...prev,
      ranks: prev.ranks.map((r) => (r.rank === rank ? { ...r, students: r.students.filter((_, i) => i !== idx) } : r)),
    }));
  };
  const addStudent = (rank, studentId) => {
    const st = roster.find((s) => s.student_id === studentId);
    if (!st) return;
    setData((prev) => ({
      ...prev,
      ranks: prev.ranks.map((r) => (r.rank === rank && r.students.length < 3 ? { ...r, students: [...r.students, { ...st }] } : r)),
    }));
  };

  const doExport = async (kind) => {
    if (!posterRef.current) return;
    setExporting(true);
    try {
      const title = `${data.exam_short}-Toppers-${className}-${section}`;
      if (kind === "pdf") await exportPdf(posterRef.current, title, "landscape");
      else await exportImage(posterRef.current, title, kind);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="toppers-generator">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onBack} data-testid="toppers-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Trophy className="h-5 w-5 text-amber-500" /> Class Toppers Announcement
          </div>
          <p className="text-sm text-slate-500">Auto-fills from exam marks. Edit if needed, then download a print-ready A4 poster.</p>
        </div>
      </div>

      {/* Selectors */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Class · Section</Label>
          <Select value={sectionKey} onValueChange={setSectionKey}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="toppers-section"><SelectValue placeholder="Select class-section" /></SelectTrigger>
            <SelectContent>
              {(options?.sections || []).map((s) => (
                <SelectItem key={`${s.class_name}|${s.section}`} value={`${s.class_name}|${s.section}`}>
                  {["Play Group", "LKG", "UKG"].includes(s.class_name) ? s.class_name : `Class ${s.class_name}`} · {s.section}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Examination</Label>
          <Select value={examKey} onValueChange={setExamKey}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="toppers-exam"><SelectValue placeholder="Select exam" /></SelectTrigger>
            <SelectContent>
              {(options?.exams || []).map((e) => (
                <SelectItem key={e.key} value={e.key}>{e.short} — {e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button className="rounded-full" onClick={load} disabled={loading} data-testid="toppers-load">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Load Toppers
        </Button>
      </div>

      {data && (
        <div className="grid lg:grid-cols-[380px_1fr] gap-5">
          {/* Editor panel */}
          <div className="space-y-3">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Academic Year</Label>
              <Input value={data.academic_year || ""} onChange={(e) => setData({ ...data, academic_year: e.target.value })} className="rounded-xl" data-testid="toppers-academic-year" />
              <Label className="text-xs uppercase tracking-wide text-slate-500 pt-1">Note / Message <span className="normal-case text-slate-400">(optional — shows on the poster)</span></Label>
              <Input value={data.note || ""} onChange={(e) => setData({ ...data, note: e.target.value })} className="rounded-xl" placeholder="e.g. Congratulations to all our shining stars!" data-testid="toppers-note" />
            </div>

            {data.ranks.map((r) => (
              <div key={r.rank} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900 space-y-3">
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{RANK_LABEL[r.rank]} <span className="text-slate-400 font-normal">· up to 3</span></div>
                {r.students.length === 0 && <div className="text-xs text-slate-400">No students. Add below.</div>}
                {r.students.map((s, i) => (
                  <div key={s.student_id || i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-2.5 space-y-2" data-testid={`edit-card-${r.rank}-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={s.name} onChange={(e) => updateStudent(r.rank, i, "name", e.target.value)} className="rounded-lg h-9" placeholder="Student name" data-testid={`edit-name-${r.rank}-${i}`} />
                      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-rose-500" onClick={() => removeStudent(r.rank, i)} data-testid={`edit-remove-${r.rank}-${i}`}><X className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={s.roll_number || ""} onChange={(e) => updateStudent(r.rank, i, "roll_number", e.target.value)} className="rounded-lg h-9" placeholder="Roll" />
                      <div className="flex items-center gap-1">
                        <Input type="number" value={s.percentage ?? ""} onChange={(e) => updateStudent(r.rank, i, "percentage", e.target.value === "" ? "" : Number(e.target.value))} className="rounded-lg h-9 w-24" placeholder="%" data-testid={`edit-pct-${r.rank}-${i}`} />
                        <span className="text-slate-400 text-sm">%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={s.class_name != null ? `${s.class_name}${s.section ? "-" + s.section : ""}` : ""} onChange={(e) => { const v = e.target.value; const [cn, sec] = v.split("-"); updateStudent(r.rank, i, "class_name", cn || ""); updateStudent(r.rank, i, "section", sec || ""); }} className="rounded-lg h-9" placeholder="Class e.g. 7-A" data-testid={`edit-class-${r.rank}-${i}`} />
                      <div className="flex items-center gap-1">
                        <Input type="number" value={s.marks ?? ""} onChange={(e) => updateStudent(r.rank, i, "marks", e.target.value === "" ? "" : Number(e.target.value))} className="rounded-lg h-9 w-20" placeholder="Marks" data-testid={`edit-marks-${r.rank}-${i}`} />
                        <span className="text-slate-400 text-sm">/</span>
                        <Input type="number" value={s.out_of ?? ""} onChange={(e) => updateStudent(r.rank, i, "out_of", e.target.value === "" ? "" : Number(e.target.value))} className="rounded-lg h-9 w-20" placeholder="Out" data-testid={`edit-outof-${r.rank}-${i}`} />
                      </div>
                    </div>
                  </div>
                ))}
                {r.students.length < 3 && (
                  <Select value="" onValueChange={(v) => addStudent(r.rank, v)}>
                    <SelectTrigger className="rounded-lg h-9" data-testid={`add-student-${r.rank}`}><SelectValue placeholder="+ Add student" /></SelectTrigger>
                    <SelectContent>
                      {roster.filter((s) => !usedIds.has(s.student_id)).map((s) => (
                        <SelectItem key={s.student_id} value={s.student_id}>{s.name} ({s.roll_number || "—"}) · {s.percentage}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>

          {/* Preview + export */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("pdf")} disabled={exporting} data-testid="toppers-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("png")} disabled={exporting} data-testid="toppers-export-png"><ImageIcon className="h-4 w-4 mr-1" /> PNG</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => doExport("jpeg")} disabled={exporting} data-testid="toppers-export-jpg"><Download className="h-4 w-4 mr-1" /> JPG</Button>
              {exporting && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
            </div>
            <div ref={previewWrapRef} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 p-4 overflow-hidden" data-testid="toppers-preview-surface">
              <div className="mx-auto shadow-xl" style={{ width: 1123 * scale, height: 794 * scale }}>
                <div style={{ width: 1123, height: 794, transform: `scale(${scale})`, transformOrigin: "top left" }}>
                  <ClassToppersPoster ref={posterRef} data={data} apiBase={API_BASE} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
